'use client';

/**
 * Interstitial slide renderer. Pure JSX given a slide config + an
 * onContinue callback. The 'processing' slot auto-advances after
 * 1.5s via setTimeout (client-only, fine in a 'use client' island);
 * other slots wait for a tap.
 */

import { useEffect } from 'react';
import {
  ActivityIcon,
  BatteryCharging,
  CircleDashed,
  Clock,
  Droplets,
  Heart,
  HeartPulse,
  Leaf,
  Moon,
  Sparkles,
  Sun,
  Zap,
  type LucideIcon,
} from 'lucide-react';
// Track 3.13-phase-0: ShieldAlert + ShieldCheck dropped from imports —
// ShieldAlert was only on after_habits_smoking (deleted), ShieldCheck
// only on trust_default (deleted).
import { PrimaryButton } from './StartUI';
import { fireFunnelEvent } from './funnel-events';
import type {
  InterstitialAccent,
  InterstitialIcon,
  InterstitialSlide,
} from '@/lib/health-profile/interstitial-content';

const ICONS: Record<InterstitialIcon, LucideIcon> = {
  'battery-charging': BatteryCharging,
  moon: Moon,
  sparkles: Sparkles,
  'heart-pulse': HeartPulse,
  heart: Heart,
  clock: Clock,
  activity: ActivityIcon,
  'circle-dashed': CircleDashed,
  droplets: Droplets,
  zap: Zap,
  leaf: Leaf,
  sun: Sun,
};

const ACCENT_BG: Record<InterstitialAccent, string> = {
  amber:   'bg-amber-100 text-amber-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  navy:    'bg-slate-200 text-[#0f2534]',
};

const ACCENT_HEADLINE: Record<InterstitialAccent, string> = {
  amber:   'text-amber-900',
  emerald: 'text-emerald-900',
  navy:    'text-[#0f2534]',
};

// Phase 2 (Lock 5h): per-slot eyebrow line + premium chrome. Each
// interstitial gets a short uppercase eyebrow above the headline,
// matching the question-step register from Lock 5a.
const SLOT_EYEBROW: Record<string, string> = {
  after_concerns: 'YOUR PROFILE — WHY THIS MATTERS',
  after_habits:   'SMALL DAILY CHOICES COMPOUND',
  processing:     'PERSONALIZING YOUR PLAN',
};
// Track 3.13-phase-0: 'trust' eyebrow dropped — slot removed.

export function Interstitial({
  slide, onContinue, anchorQuestionId, displayNumber, themeColor = '#0f1e3d',
}: {
  slide: InterstitialSlide;
  onContinue: () => void;
  /** Question step id the interstitial sits after (drives the
   *  anchor_question_id field on funnel_events). */
  anchorQuestionId: string;
  /** Question-step display number this interstitial inherits from. */
  displayNumber: number;
  /** Phase 2: theme color drives the icon bubble + eyebrow + Continue
   *  CTA tint. Inherited from the next data step the interstitial
   *  precedes via QuizThemeProvider. */
  themeColor?: string;
}) {
  const Icon = ICONS[slide.iconName] ?? Sparkles;
  const isProcessing = slide.slot === 'processing';
  const eyebrow = SLOT_EYEBROW[slide.slot];

  // Track 3.13-phase-0: impression event on mount. Fires once per
  // slide.id transition; the parent's keyed wrapper guarantees this
  // component remounts when navigating between interstitials.
  useEffect(() => {
    fireFunnelEvent({
      eventType: 'impression',
      slot: slide.slot,
      slideId: slide.id,
      anchorQuestionId,
      displayNumber,
    });
  }, [slide.id, slide.slot, anchorQuestionId, displayNumber]);

  // Processing slide auto-advances after 1.5s. setTimeout cleanup on
  // unmount or onContinue change covers React strict-mode + back
  // navigation cases. Track 3.13-phase-0: fires processing_auto_complete
  // when the timer triggers (not when the component unmounts via Back).
  useEffect(() => {
    if (!isProcessing) return;
    const t = setTimeout(() => {
      fireFunnelEvent({
        eventType: 'processing_auto_complete',
        slot: slide.slot,
        slideId: slide.id,
        anchorQuestionId,
        displayNumber,
      });
      onContinue();
    }, 1500);
    return () => clearTimeout(t);
  }, [isProcessing, onContinue, slide.id, slide.slot, anchorQuestionId, displayNumber]);

  // Track 3.13-phase-0: CTA event fires before onContinue so the event
  // dispatches even if the parent's onContinue triggers an immediate
  // route push (keepalive:true on the fetch covers the unload race).
  const handleContinue = () => {
    fireFunnelEvent({
      eventType: 'cta_continue',
      slot: slide.slot,
      slideId: slide.id,
      anchorQuestionId,
      displayNumber,
    });
    onContinue();
  };

  return (
    <section
      aria-label={slide.headline}
      className="bg-[#fefaf6] rounded-2xl border border-amber-100 p-8 md:p-10 text-center space-y-6"
    >
      <div className={['mx-auto h-24 w-24 rounded-full flex items-center justify-center', ACCENT_BG[slide.accent]].join(' ')}>
        <Icon size={40} strokeWidth={1.6} aria-hidden />
      </div>
      {eyebrow && (
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.22em]"
          style={{ color: themeColor }}
        >
          {eyebrow}
        </p>
      )}
      <h2 className={['text-3xl md:text-4xl font-normal leading-tight tracking-tight font-[family-name:var(--font-editorial)]', ACCENT_HEADLINE[slide.accent]].join(' ')}>
        {slide.headline}
      </h2>
      <p className="text-base md:text-lg text-slate-700 leading-relaxed max-w-prose mx-auto">
        {slide.body}
      </p>
      {/* Track 3.13-phase-0: trust-pill row deleted — was conditioned on
          slide.slot === 'trust' but the trust slot was removed from
          buildFlow Phase 1. Trust signals belong on the Phase 3 proof
          slot via the reusable TrustBadgeRow component. */}
      {isProcessing ? (
        <div className="pt-2 flex justify-center" aria-hidden>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-[pulse_1.2s_ease-in-out_infinite] [animation-delay:0ms]" />
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-[pulse_1.2s_ease-in-out_infinite] [animation-delay:200ms]" />
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-[pulse_1.2s_ease-in-out_infinite] [animation-delay:400ms]" />
          </span>
        </div>
      ) : (
        <div className="pt-2">
          {/* Lock 5h: orange tone matches the funnel Add-all CTA, not
              the navy primary used on question steps.
              Track 3.13-phase-0: onClick wraps onContinue so cta_continue
              fires before navigation. */}
          <PrimaryButton label="Continue →" onClick={handleContinue} tone="orange" />
        </div>
      )}
    </section>
  );
}
